import { UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form'
import { Device, Emc, InstallationFormModel, Storage } from '../../installation/forms'
import { getStorageTypeFromPairingId } from '../../parsers/device-parsers/device-parsers'
import { logError } from '../../utils/logger'
import { useInstallationForm } from '../../installation/hooks'
import useModals from 'src/Hooks/useModals'

import { EebusPairingDialogProps, ModbusTcpPairingDialogProps } from '../charging-stations/components/Dialogs'
import { HeatpumpPairingDialogProps } from '../heatpump/components/Dialogs/HeatpumpPairingDialog'
import { Htc320hIoPairingDialogProps, MyPvPairingDialogProps } from '../io/components/Dialogs'
import { EmcPairingDialogProps } from '../overview'
import { MeterPairingDialogProps } from '../powermeter/components/Dialog/MeterPairingDialog'
import { StoragePairingDialogProps } from '../storage/components'

type PairingHandler = { canPair: boolean; handlePairing: () => void }
type PairingDialogs = {
  openEmcPairingDialog: (props: EmcPairingDialogProps) => void
  openStoragePairingDialog: (props: StoragePairingDialogProps) => void
  openModbusTcpPairingDialog: (props: ModbusTcpPairingDialogProps) => void
  openEebusPairingDialog: (props: EebusPairingDialogProps) => void
  openHeatpumpPairingDialog: (props: HeatpumpPairingDialogProps) => void
  openMeterPairingDialog: (props: MeterPairingDialogProps) => void
  openHtc320hIoPairingDialog: (props: Htc320hIoPairingDialogProps) => void
  openMyPvPairingDialog: (props: MyPvPairingDialogProps) => void
}
type Utilities = {
  dialogs: PairingDialogs
  getValues: UseFormGetValues<InstallationFormModel>
  setValue: UseFormSetValue<InstallationFormModel>
}

/**
 * Function factory to get the right pairing handler function for each device category.
 */
export const useDevicePairing = (device: Device | null, storageIndex?: number): PairingHandler => {
  const {
    openEmcPairingDialog,
    openStoragePairingDialog,
    openModbusTcpPairingDialog,
    openEebusPairingDialog,
    openHeatpumpPairingDialog,
    openMeterPairingDialog,
    openHtc320hIoPairingDialog,
    openMyPvPairingDialog,
  } = useModals()

  const dialogs = {
    openEmcPairingDialog,
    openStoragePairingDialog,
    openModbusTcpPairingDialog,
    openEebusPairingDialog,
    openHeatpumpPairingDialog,
    openMeterPairingDialog,
    openHtc320hIoPairingDialog,
    openMyPvPairingDialog,
  }

  const { control, getValues, setValue } = useInstallationForm()

  const installationId = useWatch({ control, name: 'id' })
  const emc = useWatch({ control, name: 'emcDevice' })
  const storages = useWatch({ control, name: 'storageDevices' })
  const resolvedStorageIndex = storageIndex ?? storages.findIndex(storage => storage.id === device?.id)
  const storage = resolvedStorageIndex >= 0 ? storages[resolvedStorageIndex] : null

  const utilities: Utilities = {
    dialogs,
    getValues,
    setValue,
  }

  if (!device) return { canPair: false, handlePairing: () => void 0 }

  switch (device.deviceCategory) {
    case 'EMC':
      return getEmcPairing(emc, utilities)
    case 'Storage':
      return getStoragePairing(storage, resolvedStorageIndex >= 0 ? resolvedStorageIndex : undefined, utilities)
    case 'ModbusTCP Evse':
      return getModbusTcpEvsePairing(installationId, emc, device, utilities)
    case 'EEBus Evse':
      return getEebusEvsePairing(installationId, emc, device, utilities)
    case 'Heatpump':
      return getHeatpumpPairing(installationId, emc, device, utilities)
    case 'Meter':
      return getMeterPairing(installationId, emc, device, utilities)
    case 'HTC320H I/O':
      return getHtc320hIoPairing(device, utilities)
    case 'Storage I/O':
      return { canPair: false, handlePairing: () => void 0 }
    case 'MyPV I/O':
      return getMyPvIoPairing(device, utilities)

    default:
      return { canPair: false, handlePairing: () => void 0 }
  }
}

const getEmcPairing = (emc: Emc | null, utilities: Utilities): PairingHandler => {
  const { dialogs, setValue } = utilities
  const { openEmcPairingDialog } = dialogs

  const canPair = !!emc
  const handlePairing = (): void => {
    if (!canPair) return
    openEmcPairingDialog({
      emc,
      submitPairingId: (id: string) => {
        setValue('emcDevice.pairingId', id, { shouldDirty: true })
      },
    })
  }
  return { canPair, handlePairing }
}

const getStoragePairing = (storage: Storage | null, storageIndex: number | undefined, utilities: Utilities): PairingHandler => {
  const { dialogs, getValues, setValue } = utilities
  const { openStoragePairingDialog } = dialogs

  const canPair = !!storage
  const handlePairing = (): void => {
    if (!canPair) return
    openStoragePairingDialog({
      emc: getValues('emcDevice'),
      installationId: getValues('id'),
      storage,
      submitPairingId: (pairingId: string) => {
        if (storageIndex === undefined) return
        const storageType = getStorageTypeFromPairingId(pairingId)
        setValue(`storageDevices.${storageIndex}.deviceType`, storageType, {
          shouldDirty: true,
        })
        setValue(`storageDevices.${storageIndex}.pairingId`, pairingId, { shouldDirty: true })
        // Fix max values for R1 type storage
        if (storageType === 'XEM - R1') {
          const maximumChargingPower = getValues(
            `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.maximumChargingPower`
          )
          if (typeof maximumChargingPower === 'number' && maximumChargingPower > 3000)
            setValue(`storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.maximumChargingPower`, 3000)

          const maximumDischargingPower = getValues(
            `storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.maximumDischargingPower`
          )
          if (typeof maximumDischargingPower === 'number' && maximumDischargingPower > 3000)
            setValue(`storageSystem.storageSpecificSettings.${storageIndex}.advancedSettings.batteryOperationRange.maximumDischargingPower`, 3000)
        }
      },
    })
  }
  return { canPair, handlePairing }
}

const getModbusTcpEvsePairing = (installationId: number | null, emc: Emc | null, modbusTcpDevice: Device, utilities: Utilities): PairingHandler => {
  const { dialogs, getValues, setValue } = utilities
  const { openModbusTcpPairingDialog } = dialogs

  const canPair = !!(installationId && emc?.deviceId && modbusTcpDevice.deviceId)
  const handlePairing = (): void => {
    if (!canPair) return
    const index = getValues('chargingStations.modbusTcp').findIndex(evse => evse.id === modbusTcpDevice.id)

    openModbusTcpPairingDialog({
      installationId,
      emc,
      modbusTcp: getValues(`chargingStations.modbusTcp.${index}`),
      modbusTcpChargingStations: getValues('chargingStations.modbusTcp'),
      submitResult: ({ pairingId, reference, macOriginVerified }) => {
        setValue(`chargingStations.modbusTcp.${index}.pairingId`, pairingId, { shouldDirty: true })
        setValue(`chargingStations.modbusTcp.${index}.deviceReference`, reference, {
          shouldDirty: true,
        })
        setValue(`chargingStations.modbusTcp.${index}.macOriginVerified`, macOriginVerified, {
          shouldDirty: true,
        })
      },
    })
  }
  return { canPair, handlePairing }
}

const getEebusEvsePairing = (installationId: number | null, emc: Emc | null, eebusDevice: Device, utilities: Utilities): PairingHandler => {
  const { dialogs, getValues, setValue } = utilities
  const { openEebusPairingDialog } = dialogs

  const canPair = !!(installationId && emc?.deviceId && eebusDevice.deviceId)
  const handlePairing = (): void => {
    if (!canPair) return
    const index = getValues('chargingStations.eebus').findIndex(evse => evse.id === eebusDevice.id)
    openEebusPairingDialog({
      installationId,
      emc,
      eebus: getValues(`chargingStations.eebus.${index}`),
      submitPairingId: (pairingId: string) => {
        setValue(`chargingStations.eebus.${index}.pairingId`, pairingId, { shouldDirty: true })
      },
    })
  }
  return { canPair, handlePairing }
}

const getHeatpumpPairing = (installationId: number | null, emc: Emc | null, heatpumpDevice: Device, utilities: Utilities): PairingHandler => {
  const { dialogs, getValues, setValue } = utilities
  const { openHeatpumpPairingDialog } = dialogs

  const canPair = !!(installationId && emc?.deviceId && emc.isPaired && heatpumpDevice.deviceId)
  const handlePairing = (): void => {
    if (!canPair) return

    const heatpump = getValues('heatpump')
    if (heatpump === null) {
      logError('Could not open heatpump pairing editor. Heatpump is null')
      return
    }

    openHeatpumpPairingDialog({
      installationId,
      emc,
      heatpump,
      submitPairingId: (pairingId: string) => {
        setValue('heatpump.pairingId', pairingId, { shouldDirty: true })
      },
    })
  }
  return { canPair, handlePairing }
}

const getMeterPairing = (installationId: number | null, emc: Emc | null, meterDevice: Device, utilities: Utilities): PairingHandler => {
  const { dialogs, getValues, setValue } = utilities
  const { openMeterPairingDialog } = dialogs

  const canPair = !!(installationId && emc?.deviceId)
  const handlePairing = (): void => {
    if (!canPair) return

    const meters = getValues('meters')
    const index = meters.findIndex(meter => meter.id === meterDevice.id)
    openMeterPairingDialog({
      meter: getValues(`meters.${index}`),
      meters,
      htc320hIoPairingId: getValues('ioInterfaces.htc320h.pairingId'),
      submitPairingId: (pairingId: string) => {
        setValue(`meters.${index}.pairingId`, pairingId, { shouldDirty: true })
      },
    })
  }
  return { canPair, handlePairing }
}

const getHtc320hIoPairing = (htc320hIoDevice: Device, utilities: Utilities): PairingHandler => {
  const { dialogs, getValues, setValue } = utilities
  const { openHtc320hIoPairingDialog } = dialogs

  const canPair = !!htc320hIoDevice
  const handlePairing = (): void => {
    if (!canPair) return
    const htc320hIo = getValues('ioInterfaces.htc320h')
    if (!htc320hIo) return

    openHtc320hIoPairingDialog({
      htc320hIo,
      meters: getValues('meters'),
      submitPairingId: newId => {
        setValue('ioInterfaces.htc320h.pairingId', newId, { shouldDirty: true })
      },
    })
  }
  return { canPair, handlePairing }
}

const getMyPvIoPairing = (myPvDevice: Device, utilities: Utilities): PairingHandler => {
  const { dialogs, getValues, setValue } = utilities
  const { openMyPvPairingDialog } = dialogs

  const canPair = !!myPvDevice
  const handlePairing = (): void => {
    if (!canPair) return
    const myPv = getValues('ioInterfaces.mypv')
    if (!myPv) return

    openMyPvPairingDialog({
      installationId: getValues('id'),
      emc: getValues('emcDevice'),
      myPv,
      submitPairingId: (pairingId: string) => {
        setValue('ioInterfaces.mypv.pairingId', pairingId, { shouldDirty: true })
      },
    })
  }
  return { canPair, handlePairing }
}
